import React, { useState } from 'react'
import ReactGA from 'react-ga'
import { Mutation } from 'react-apollo'
import { Redirect, Link } from 'react-router-dom'
import { GetCartQuery } from '../CartQuery/CartQuery'
import ReactRouterPropTypes from 'react-router-prop-types'
import { NetworkStatus } from 'apollo-client'

import { useOOGlobals } from '../OOGlobalsProvider/OOGlobalsProvider'
import { PLACE_ORDER, GET_CART_FOR_CHECKOUT, GET_COMPLETED_ORDER } from '../../apollo/cart/cart.graphql'
import { CREATE_ACCOUNT } from '../../apollo/authentication/authentication.graphql'
import { dataByTypename } from '../../utils/apollo-helpers'
import CloseButton from '../CloseButton/CloseButton'
import Error from '../Error/Error'
import Progress from '../Progress/Progress'
import { useAuth } from '../AuthProvider/AuthProvider'
import { CREDIT_CARDS } from '../CreditCardProvider/CreditCard.graphql'
import { SAVED_ADDRESSES } from '../../apollo/saved-addresses.graphql'
import RestaurantInfo from '../RestaurantInfo/RestaurantInfo'
import ScrollToTop from '../ScrollToTop/ScrollToTop'
import CheckoutForm from '../CheckoutForm/CheckoutForm'
import { RestaurantConsumer } from '../RestaurantProvider/RestaurantProvider'
import { CreditCardProvider } from '../CreditCardProvider/CreditCardProvider'
import { ToastContainer } from 'react-toastify'
import { useFlag } from '../FeatureFlag/use-flag'

import style from './CheckoutPage.css'
import { CheckoutSelector, CHECKOUT_METHODS } from './CheckoutSelector/CheckoutSelector'
import { WrapIfFlagOn } from '../FeatureFlag/FeatureFlag'
import { CheckoutApplePay } from '../CheckoutApplePay/CheckoutApplePay'
import { useTrackPurchase } from '../../utils/checkout-helpers'
import { useServiceAvailability, SERVICES } from '../ServiceAvailabilityProvider/ServiceAvaialbilityProvider'

const { toastContainer, toastWrapper } = style

const handleCreateAccount = async ({ createAccount, ccInput, password, redirectUrl, restaurantGuid, setErrorMessage, history, setUnverifiedCustomer }) => {
  const createAccountInput = {
    email: ccInput.customer.email,
    password,
    redirectUrl,
    registrationClient: 'ONLINE_ORDERING',
    restaurantGuid,
    basicInfo: {
      firstName: ccInput.customer.firstName,
      lastName: ccInput.customer.lastName,
      phone: ccInput.customer.phone
    }
  }

  const accountCreationResp = await createAccount({ variables: { input: createAccountInput } })
  const { CreateAccountResponse, CreateAccountError } = dataByTypename(accountCreationResp.data.createAccount)
  if (CreateAccountError) {
    if (CreateAccountError.code === 'EMAIL_IN_USE') {
      history.push('?mode=login', { createAccountRedirect: true })
    } else {
      setErrorMessage(CreateAccountError)
    }
  } else {
    setUnverifiedCustomer({ guid: CreateAccountResponse.customerGuid, signupSource: 'SAVE_CREDIT_CARD' })
    return CreateAccountResponse.customerGuid
  }
}

const handlePlaceOrder = async ({ placeOrder, cart, cashInput, ccInput, paymentType, getRestaurantPath, customerGuid,
  deleteCartGuid, setErrorMessage, history, eventLabel, trackPurchase, logCriticalError }) => {
  if (paymentType === 'CREDIT') {
    ccInput.cartGuid = cart.guid
    if (customerGuid) {
      ccInput.customerGuid = customerGuid
    }
  } else {
    cashInput.cartGuid = cart.guid
  }

  const resp = await placeOrder({ variables: { cashInput, ccInput, paymentType } })
  const { PlaceOrderResponse, PlaceOrderError, PlaceOrderCartUpdatedError } = dataByTypename(resp.data.placeOrder)
  const error = PlaceOrderError || PlaceOrderCartUpdatedError
  if (error) {
    if (error.code === 'CRITICAL_ERROR') {
      logCriticalError(SERVICES.PLACE_ORDER)
    }
    setErrorMessage(error)
  } else {
    const { guid } = PlaceOrderResponse.completedOrder
    trackPurchase(PlaceOrderResponse.completedOrder)
    ReactGA.event({
      category: 'Checkout',
      action: 'Place Order Success',
      label: eventLabel
    })
    deleteCartGuid()
    history.replace(getRestaurantPath(`confirm/${guid}`))
  }
}

const getEventLabel = (authenticated, customerGuid) => {
  if (customerGuid) return 'new account'
  return authenticated ? 'authenticated' : 'unauthenticated'
}

const CheckoutPage = ({ history }) => {
  const trackPurchase = useTrackPurchase()
  const { toastwebBaseUri, routerBasename } = useOOGlobals()
  const { authenticated, setUnverifiedCustomer } = useAuth()
  const [errorMessage, setErrorMessage] = useState()
  const useDelivery = useFlag('oo-v3-delivery')
  const usePromoCodes = useFlag('oo-v3-promocodes')
  const { logCriticalError } = useServiceAvailability()

  return (
    <CreditCardProvider>
      <RestaurantConsumer>
        {({ getRestaurantPath, restaurantGuid }) => (
          <div styleName='page'>
            <ScrollToTop />
            <RestaurantInfo header='Checkout' />
            <div styleName='cartButton'>
              <CloseButton data-testid='close-button-checkout-page'to={getRestaurantPath('cart')} />
            </div>
            <Link to={getRestaurantPath()} data-testid='back-to-menu-link' styleName='menuButton'>Back to Menu</Link>
            <GetCartQuery query={GET_CART_FOR_CHECKOUT}>
              {({ error: getCartError, loading, cart, deleteCartGuid, refetch, networkStatus }) => {
                if (loading || networkStatus === NetworkStatus.refetch) { return <Progress eventData={{ category: 'Checkout Page' }} /> }
                if (getCartError) { return <Error error={getCartError} retry={refetch} /> }
                if (!cart) { return <Redirect to={getRestaurantPath()} replace /> }
                return (
                  <Mutation
                    mutation={PLACE_ORDER}
                    refetchQueries={() => {
                      const toRefetch = []

                      // TODO Can we do something in `update` to just remove from the cache
                      // instead of proactively refetching CREDIT_CARDS immediately since we
                      // don't need it right away.

                      // Needed in case user saved a new card or address.
                      if (authenticated) {
                        toRefetch.push({
                          query: CREDIT_CARDS
                        })

                        if (useDelivery === true) {
                          toRefetch.push({
                            query: SAVED_ADDRESSES
                          })
                        }
                      }

                      return toRefetch
                    }}
                    update={(cache, { data: { placeOrder } }) => {
                      if (placeOrder.completedOrder) {
                        cache.writeQuery({
                          query: GET_COMPLETED_ORDER,
                          data: { completedOrder: placeOrder.completedOrder },
                          variables: { input: {
                            orderGuid: placeOrder.completedOrder.guid,
                            restaurantGuid
                          }}
                        })
                        cache.deleteKeyByRegex(/^\$ROOT_QUERY\.paginatedOrders/)
                      }
                    }}>
                    {(placeOrder, { data, loading: placeOrderLoading, error: placeOrderError }) => (
                      <React.Fragment>
                        <Mutation mutation={CREATE_ACCOUNT}>
                          {(createAccount) => (
                            <React.Fragment>
                              <WrapIfFlagOn
                                name='oo-v3-apple-pay'
                                wrapper={(children) => <CheckoutSelector>{method => children(method)}</CheckoutSelector>}
                                defaultValue={false}
                              >
                                {method =>
                                  <React.Fragment>
                                    {method === CHECKOUT_METHODS.APPLE_PAY
                                      ? <CheckoutApplePay />
                                      : <CheckoutForm
                                        promoCodesEnabled={usePromoCodes}
                                        error={placeOrderError || errorMessage}
                                        cart={cart}
                                        paymentOptions={cart.paymentOptions}
                                        preComputedTips={cart.preComputedTips}
                                        onSubmit={async ({ cashInput, ccInput, paymentType, password }) => {
                                          const redirectUrl = `${toastwebBaseUri}${routerBasename}?mode=verify`
                                          let customerGuid
                                          if (
                                            ccInput &&
                                          ccInput.newCardInput &&
                                          ccInput.newCardInput.saveCard &&
                                          password
                                          ) {
                                            try {
                                              customerGuid = await handleCreateAccount({
                                                createAccount,
                                                ccInput,
                                                password,
                                                redirectUrl,
                                                restaurantGuid,
                                                setErrorMessage,
                                                history,
                                                setUnverifiedCustomer
                                              })
                                              ReactGA.event({
                                                category: 'User Account',
                                                action: 'Create Account Success',
                                                label: 'During Checkout'
                                              })
                                            } catch (err) {
                                              console.warn(err)
                                              return
                                            }
                                            if (!customerGuid) { // If account wasn't successfully created, don't place the order
                                              return
                                            }
                                          }
                                          const eventLabel = getEventLabel(authenticated, customerGuid)

                                          try {
                                            await handlePlaceOrder({
                                              placeOrder,
                                              cart,
                                              cashInput,
                                              ccInput,
                                              paymentType,
                                              eventLabel,
                                              getRestaurantPath,
                                              deleteCartGuid,
                                              setErrorMessage,
                                              customerGuid,
                                              history,
                                              trackPurchase,
                                              logCriticalError
                                            })
                                          } catch (err) {
                                            console.warn(err)
                                          }
                                        }} />
                                    }
                                  </React.Fragment>

                                }
                              </WrapIfFlagOn>
                            </React.Fragment>
                          )}
                        </Mutation>
                      </React.Fragment>) }
                  </Mutation>)
              }}
            </GetCartQuery>
          </div>
        )}
      </RestaurantConsumer>
      <ToastContainer
        hideProgressBar
        closeButton={false}
        className={toastContainer}
        toastClassName={toastWrapper}
        position='top-center'
      />
    </CreditCardProvider>
  )
}

CheckoutPage.propTypes = {
  history: ReactRouterPropTypes.history.isRequired
}

export default CheckoutPage
